import React, { useEffect, useMemo, useState } from 'react'
import { analysisApi } from '../../services/analysis'
import { BrandHeading } from '../../components/lease/BrandHeading'
import { PrimaryButton } from '../../components/lease/PrimaryButton'

// Lease Context Check (required before scoring): some lease terms depend on
// facts the document doesn't show — e.g. a $0.00 deposit line reads very
// differently on a renewal than on a new lease.
function isRequired(q, answers) {
  if (q.required_if) {
    return Object.entries(q.required_if).every(([k, v]) => answers[k] === v)
  }
  // required_if_any: condition met when the other answer is ANY of a list —
  // the property-class follow-ups apply to several property types at once.
  if (q.required_if_any) {
    return Object.entries(q.required_if_any).every(([k, vs]) => vs.includes(answers[k]))
  }
  return !!q.required
}

function isVisible(q, answers) {
  // conditional questions only appear once their condition is met
  return q.required_if || q.required_if_any ? isRequired(q, answers) : true
}

export function ContextCheck({ documentId, onComplete }) {
  const [def, setDef] = useState(null)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    analysisApi.getContextQuestions().then(setDef)
  }, [])

  const complete = useMemo(() => {
    if (!def) return false
    return def.questions.every((q) => {
      if (!isRequired(q, answers)) return true
      const v = answers[q.id]
      return q.type === 'multi_select' ? Array.isArray(v) && v.length > 0 : !!v
    })
  }, [def, answers])

  const setSingle = (id, value) => setAnswers((a) => ({ ...a, [id]: value }))
  const toggleMulti = (id, value) =>
    setAnswers((a) => {
      const cur = new Set(a[id] || [])
      cur.has(value) ? cur.delete(value) : cur.add(value)
      return { ...a, [id]: [...cur] }
    })

  const submit = async () => {
    setSaving(true)
    setErrors(null)
    try {
      // drop answers to questions that are no longer visible (condition unmet)
      const visible = Object.fromEntries(
        Object.entries(answers).filter(([k]) => {
          const q = def.questions.find((x) => x.id === k)
          return q && isVisible(q, answers)
        })
      )
      await analysisApi.saveContext(documentId, visible)
      onComplete()
    } catch (e) {
      const detail = e.response?.data?.detail
      setErrors(Array.isArray(detail) ? detail : [e.message])
    } finally {
      setSaving(false)
    }
  }

  if (!def) return <p className="text-center text-slate-600 dark:text-slate-300">Loading questions…</p>

  return (
    <div>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-3xl">A few questions</BrandHeading>
        <p className="mt-2 text-slate-600 dark:text-slate-300">{def.consumer_intro}</p>
      </header>

      <div className="mt-6 animate-slide-in space-y-4">
        {def.questions.filter((q) => isVisible(q, answers)).map((q) => (
          <fieldset
            key={q.id}
            className="rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700"
          >
            <legend className="text-sm font-semibold text-slate-800 dark:text-slate-100">{q.label}</legend>
            {q.helper_text && <p className="text-xs text-slate-500 dark:text-slate-400">{q.helper_text}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {q.options.map((o) => {
                const selected =
                  q.type === 'multi_select'
                    ? (answers[q.id] || []).includes(o.value)
                    : answers[q.id] === o.value
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      q.type === 'multi_select'
                        ? toggleMulti(q.id, o.value)
                        : setSingle(q.id, o.value)
                    }
                    className={`rounded-xl px-3 py-1.5 text-sm font-semibold transition ${
                      selected
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow'
                        : 'bg-white/70 text-slate-700 ring-1 ring-emerald-200 hover:bg-emerald-50 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700'
                    }`}
                  >
                    {o.label}
                  </button>
                )
              })}
            </div>
          </fieldset>
        ))}
      </div>

      {errors && (
        <ul className="mt-4 animate-slide-in rounded-2xl bg-rose-50 p-3 text-sm text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
          {errors.map((e, i) => (
            <li key={i}>{String(e)}</li>
          ))}
        </ul>
      )}

      <PrimaryButton
        type="button"
        disabled={!complete || saving}
        onClick={submit}
        className="mt-6 animate-slide-in"
      >
        {saving ? 'Saving…' : 'Continue to analysis'}
      </PrimaryButton>
    </div>
  )
}
